<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrVaahAgentsOrbitRings from "./WrVaahAgentsOrbitRings.vue";

export interface VaahPill {
    label: string;
    x?: string;
    y?: string;
    w?: string;
}

interface Props {
    heading?: string;
    subtitle?: string;
    ctaText?: string;
    ctaLink?: string;
    centerImage?: string;
    pills?: VaahPill[];
}

withDefaults(defineProps<Props>(), {
    heading: "Why Choose GenicUI??",
    subtitle: 'Show real <strong>frontend components</strong> directly in <strong>AI chats</strong> no extra code, instant live updates, and complete security.',
    ctaText: "Get Started Instantly",
    ctaLink: "",
    centerImage: "/images/wri-genic.webp",
    pills: () => [
        {
            label: "100% IP & Data Ownership",
            x: "42.4%",
            y: "4.6%",
            w: "125px",
        },
        { label: "Instant UI Updates", x: "63%", y: "12.1%", w: "118px" },
        {
            label: "Native Component Bridge",
            x: "19.9%",
            y: "17.5%",
            w: "158px",
        },
        {
            label: "Shadow DOM Isolation",
            x: "40.6%",
            y: "21.3%",
            w: "130px",
        },
        {
            label: "Open Source Leadership",
            x: "60.1%",
            y: "25%",
            w: "116px",
        },
        {
            label: "Framework Agnostic",
            x: "81.5%",
            y: "19.5%",
            w: "140px",
        },
        { label: "Zero UI Rewrites", x: "24.3%", y: "32.5%", w: "148px" },
        {
            label: "Enterprise Security First",
            x: "76.9%",
            y: "31.2%",
            w: "172px",
        },
        {
            label: "MCP Protocol Native",
            x: "9.5%",
            y: "40.7%",
            w: "142px",
        },
        {
            label: "Standardized Protocol",
            x: "92.5%",
            y: "38.2%",
            w: "126px",
        },
        {
            label: "Zero Custom Adapters",
            x: "71.2%",
            y: "42.2%",
            w: "154px",
        },
    ],
});

const emit = defineEmits<{
    (e: "cta-clicked"): void;
}>();
</script>

<template>
    <section class="vaah-why-choose max-sm:gap-3 relative flex flex-col items-center justify-center">
        <div class="hidden md:block relative w-[min(86vw,900px)] aspect-square pointer-events-none select-none my-auto">

            <!-- Concentric Rings & Arc Shimmers -->
            <div class="vaah-why-rings absolute inset-0" aria-hidden="true">
                <WrVaahAgentsOrbitRings />
            </div>

            <!-- Stage Upper Heading + Center Logo -->
            <div>
                <h2
                    class="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 z-30 max-w-[12rem] lg:max-w-[14rem] text-center font-avant-demi text-xl lg:text-3xl font-semibold leading-tight bg-gradient-to-r from-[#0e7490] to-[#7c3aed] dark:from-[#12DBFF] dark:to-[#895AF5] bg-clip-text text-transparent">
                    {{ heading }}
                </h2>
                <NuxtImg :src="centerImage" alt="WebReinvent" width="630" height="467" loading="eager"
                    class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120px] lg:w-[144px] h-auto z-10" />
            </div>

            <!-- Glassmorphism Panel (Bottom Half) -->
            <div
                class="vaah-why-panel absolute left-1/2 top-[52.5%] -translate-x-1/2 z-20 pointer-events-auto hidden md:flex flex-col items-center w-[90%] h-[36%] pt-[46px] px-[40px] pb-0 overflow-hidden">
                <span class="vaah-why-panel-top-glow" aria-hidden="true"></span>
                <p class="vaah-why-panel-subtitle font-avant max-w-[30rem] text-center text-[1.0625rem] leading-[1.6]"
                    v-html="subtitle">
                </p>
                <div class="mt-7 md:mt-12 flex justify-center">
                    <WrButton :link="ctaLink || undefined"
                        append-class="px-7 bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)]"
                        @click="emit('cta-clicked')">
                        {{ ctaText }}
                    </WrButton>
                </div>
            </div>

            <!-- Orbiting Feature Pills -->
            <div class="absolute inset-0 z-40 hidden md:block">
                <span v-for="pill in pills" :key="pill.label"
                    class="vaah-why-pill font-avant absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center text-center px-3 py-2 rounded-[10px] text-[0.75rem] lg:text-[0.8rem] font-medium tracking-[0.02em]"
                    :style="{ left: pill.x, top: pill.y, '--pill-w': pill.w }">
                    {{ pill.label }}
                </span>
            </div>
        </div>

        <!-- Mobile Layout -->
        <div class="md:hidden relative z-10 mx-auto  px-4 text-center mb-3 pb-3">
            <h2
                class="font-avant-demi text-3xl font-semibold leading-tight bg-gradient-to-r from-[#0e7490] to-[#7c3aed] dark:from-[#12DBFF] dark:to-[#895AF5] bg-clip-text text-transparent">
                {{ heading }}
            </h2>
            <div class="">
                <p class="font-avant text-base text-[#5A5478]/85 dark:text-white/85 leading-relaxed" v-html="subtitle">

                </p>
                <div class="mt-5 flex justify-center">
                    <WrButton :link="ctaLink || undefined"
                        append-class="px-7 bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)]"
                        @click="emit('cta-clicked')">
                        {{ ctaText }}
                    </WrButton>
                </div>
            </div>
        </div>

        <!-- Mobile Ring & Static Pill Grid -->
        <div class="md:hidden relative mx-auto max-w-md px-4 mt-8 w-full">
            <div class="vaah-why-mobile-orbit absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(96vw,380px)] aspect-square pointer-events-none"
                aria-hidden="true">
                <WrVaahAgentsOrbitRings />
            </div>

            <div class="relative flex flex-wrap justify-center gap-3">
                <span v-for="pill in pills" :key="'mobile-' + pill.label"
                    class="vaah-why-pill font-avant flex items-center justify-center text-center px-3.5 py-[0.55rem] rounded-[10px] text-[0.75rem] font-medium tracking-[0.02em] basis-[calc(33.333%_-_8px)]">
                    {{ pill.label }}
                </span>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* Ring Mask Filters */
.vaah-why-rings {
    -webkit-mask-image: linear-gradient(180deg, #000 35%, rgba(0, 0, 0, 0.35) 52%, transparent 70%);
    mask-image: linear-gradient(180deg, #000 35%, rgba(0, 0, 0, 0.35) 52%, transparent 70%);
}

.vaah-why-mobile-orbit {
    -webkit-mask-image: linear-gradient(180deg, #000 45%, rgba(0, 0, 0, 0.45) 65%, transparent 88%);
    mask-image: linear-gradient(180deg, #000 45%, rgba(0, 0, 0, 0.45) 65%, transparent 88%);
}

/* Glassmorphism Panel */
.vaah-why-panel::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    backdrop-filter: blur(24px) saturate(1.4);
    -webkit-backdrop-filter: blur(24px) saturate(1.4);
    -webkit-mask-image: radial-gradient(ellipse 65% 85% at 50% 32%, #000 45%, transparent 80%);
    mask-image: radial-gradient(ellipse 65% 85% at 50% 32%, #000 45%, transparent 80%);
    pointer-events: none;
}

.vaah-why-panel::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background-image:
        radial-gradient(rgba(189, 197, 255, 0.12) 1px, rgba(189, 197, 255, 0.04) 2.5px, transparent 4px),
        linear-gradient(180deg, rgba(13, 20, 30, 0.3), rgba(0, 0, 0, 0.45));
    background-size: 11px 11px, 100% 100%;
    filter: blur(2px);
    -webkit-mask-image: radial-gradient(ellipse 65% 85% at 50% 32%, #000 45%, transparent 80%);
    mask-image: radial-gradient(ellipse 65% 85% at 50% 32%, #000 45%, transparent 80%);
    pointer-events: none;
}

.vaah-why-panel-top-glow {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #bdc5ff 50%, transparent);
    filter: drop-shadow(0 0 6px rgba(189, 197, 255, 0.9)) drop-shadow(0 0 16px rgba(189, 197, 255, 0.5));
    pointer-events: none;
    transform-origin: center;
    animation: vaah-why-top-glow 4.5s ease-in-out infinite;
}

.vaah-why-panel-top-glow::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 0;
    width: 100%;
    height: 90px;
    background: radial-gradient(ellipse 75% 100% at 50% 0%, rgba(189, 197, 255, 0.2), transparent 70%);
}

@keyframes vaah-why-top-glow {
    0% {
        transform: scaleX(0);
        opacity: 0;
    }

    15% {
        opacity: 1;
    }

    60% {
        transform: scaleX(1);
        opacity: 0.9;
    }

    100% {
        transform: scaleX(1);
        opacity: 0;
    }
}

.vaah-why-panel-subtitle {
    color: rgba(255, 255, 255, 0.85);
}

.vaah-why-panel-subtitle strong {
    font-weight: 700;
    color: #fff;
}

/* Pill Component Styling */
.vaah-why-pill {
    border: 1px solid #1c2b48;
    width: var(--pill-w, auto);
    box-shadow: inset 0 1px 18.9px #0e1a36;
    background-color: #000a1f;
    background-image:
        linear-gradient(94.65deg, #bdc5ff 3.76%, #3f4783 140.86%),
        linear-gradient(#000a1f, #000a1f);
    -webkit-background-clip: text, padding-box;
    background-clip: text, padding-box;
    color: transparent;
}

/* Light Theme Overrides */
.my-app-light .vaah-why-panel::before {
    background-image:
        radial-gradient(rgba(109, 90, 220, 0.18) 1px, rgba(109, 90, 220, 0.06) 2.5px, transparent 4px),
        linear-gradient(180deg, rgba(255, 255, 255, 0.3), rgba(243, 240, 255, 0.5));
}

.my-app-light .vaah-why-panel-subtitle {
    color: rgba(30, 27, 75, 0.85);
}

.my-app-light .vaah-why-panel-subtitle strong {
    color: #4c1d95;
}

.my-app-light .vaah-why-pill {
    border-color: rgba(124, 108, 246, 0.35);
    box-shadow: inset 0 1px 18.9px rgba(93, 70, 190, 0.15);
    background-color: #ffffff;
    background-image:
        linear-gradient(94.65deg, #501d71 3.76%, #4753dc 140.86%),
        linear-gradient(#ffffff, #ffffff);
}

.my-app-light .vaah-why-panel-top-glow {
    background: linear-gradient(90deg, transparent, #8b7bff 50%, transparent);
    filter: drop-shadow(0 0 6px rgba(139, 123, 255, 0.9)) drop-shadow(0 0 16px rgba(139, 123, 255, 0.5));
}

.my-app-light .vaah-why-panel-top-glow::after {
    background: radial-gradient(ellipse 75% 100% at 50% 0%, rgba(139, 123, 255, 0.25), transparent 70%);
}

@media (prefers-reduced-motion: reduce) {
    .vaah-why-panel-top-glow {
        animation: none;
    }
}
</style>